import React from 'react';
import { ClientsFiltersState, ContactTypeFilter, SortField, SortDirection } from '../ClientsPage';
import { CONTACT_TYPES, CONTACT_TYPE_LABEL_PLURAL } from '../../../constants/contactTypes';

interface ContactsFiltersProps {
  filters: ClientsFiltersState;
  onFilterChange: (filters: Partial<ClientsFiltersState>) => void;
  onResetFilters: () => void;
  onNewClient: () => void;
  totalCount: number;
  filteredCount: number;
}

export function ClientsFilters({
  filters,
  onFilterChange,
  onResetFilters,
  onNewClient,
  totalCount,
  filteredCount,
}: ContactsFiltersProps) {
  const hasActiveFilters = 
    filters.search !== '' ||
    filters.contactType !== 'all' ||
    filters.hasActiveJobs !== null ||
    filters.hasOutstandingInvoices !== null;

  // Built from the central CONTACT_TYPES list + plural-label map.
  // Adding a new contact type only requires editing
  // constants/contactTypes.ts; this dropdown picks it up automatically.
  const contactTypeOptions: { value: ContactTypeFilter; label: string }[] = [
    { value: 'all', label: 'All Types' },
    ...CONTACT_TYPES.map((t) => ({
      value: t as ContactTypeFilter,
      label: CONTACT_TYPE_LABEL_PLURAL[t],
    })),
  ];

  const sortOptions: { value: SortField; label: string }[] = [
    { value: 'lastContacted', label: 'Last Contacted' },
    { value: 'company', label: 'Company Name' },
    { value: 'name', label: 'Contact Name' },
    { value: 'createdAt', label: 'Date Added' },
    { value: 'contactType', label: 'Type' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'recurring', label: 'Recurring Invoices' },
  ];

  return (
    <div className="card mb-5">
      <div className="card-body py-4">
        {/* Header Row */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h3 className="mb-0 fw-bold">Contact Directory</h3>
            <span className="text-muted fs-7">
              {filteredCount === totalCount 
                ? `${totalCount} contacts` 
                : `Showing ${filteredCount} of ${totalCount} contacts`}
            </span>
          </div>
          <button className="btn btn-primary" onClick={onNewClient}>
            <i className="bi bi-plus-lg me-2"></i>
            New Contact
          </button>
        </div>

        {/* Filters Row */}
        <div className="row g-3 align-items-end">
          {/* Search */}
          <div className="col-12 col-md-4 col-lg-3">
            <label className="form-label fs-7 text-muted">Search</label>
            <div className="position-relative">
              <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-muted"></i>
              <input
                type="text"
                className="form-control form-control-solid ps-10"
                placeholder="Search contacts..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
              />
              {filters.search && (
                <button
                  className="btn btn-sm btn-icon position-absolute top-50 translate-middle-y end-0 me-2"
                  onClick={() => onFilterChange({ search: '' })}
                >
                  <i className="bi bi-x-lg text-muted"></i>
                </button>
              )}
            </div>
          </div>

          {/* Contact Type */}
          <div className="col-6 col-md-3 col-lg-2">
            <label className="form-label fs-7 text-muted">Type</label>
            <select
              className="form-select form-select-solid"
              value={filters.contactType}
              onChange={(e) => onFilterChange({ contactType: e.target.value as ContactTypeFilter })}
            >
              {contactTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div className="col-6 col-md-3 col-lg-2">
            <label className="form-label fs-7 text-muted">Sort By</label>
            <select
              className="form-select form-select-solid"
              value={filters.sortField}
              onChange={(e) => onFilterChange({ sortField: e.target.value as SortField })}
            >
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Sort Direction */}
          <div className="col-auto">
            <button
              className="btn btn-icon btn-light-primary"
              onClick={() => onFilterChange({ 
                sortDirection: filters.sortDirection === 'asc' ? 'desc' : 'asc' 
              })}
              title={filters.sortDirection === 'asc' ? 'Ascending' : 'Descending'}
            >
              <i className={`bi bi-sort-${filters.sortDirection === 'asc' ? 'up' : 'down'} fs-4`}></i>
            </button>
          </div>

          {/* Quick Filters */}
          <div className="col-12 col-lg-auto ms-lg-auto">
            <div className="d-flex gap-2 flex-wrap">
              <button
                className={`btn btn-sm ${filters.hasActiveJobs === true ? 'btn-primary' : 'btn-light'}`}
                onClick={() => onFilterChange({ 
                  hasActiveJobs: filters.hasActiveJobs === true ? null : true 
                })}
              >
                <i className="bi bi-briefcase me-1"></i>
                Active Jobs
              </button>
              <button
                className={`btn btn-sm ${filters.hasOutstandingInvoices === true ? 'btn-warning' : 'btn-light'}`}
                onClick={() => onFilterChange({ 
                  hasOutstandingInvoices: filters.hasOutstandingInvoices === true ? null : true 
                })}
              >
                <i className="bi bi-clock me-1"></i>
                Outstanding
              </button>
              {hasActiveFilters && (
                <button
                  className="btn btn-sm btn-light-danger"
                  onClick={onResetFilters}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientsFilters;
